import React, { useState } from 'react';
import {
  Search, Filter, Calendar, MoreHorizontal, ChevronLeft, ChevronRight,
  Trash2, ArrowUpDown, Globe, Mail, Cloud, MousePointer2, Briefcase, 
  Database, Globe2, X, Smartphone, Image, Video, Users // Added missing imports here
} from 'lucide-react';

type OrderStatus = 'Approve' | 'Incomplete' | 'Completed' | 'Pending';

interface OrderRow {
  id: string; customer: string; service: string; date: string;
  amount: string; paymentStatus: string; status: OrderStatus; icon: React.ReactNode;
}

const OrderManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('All Orders');
  const [selectedOrder, setSelectedOrder] = useState<OrderRow | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleAddNew = () => {
    alert("Add New Order button is now working!");
  };

  const [orderList, setOrderList] = useState<OrderRow[]>([
    { id: '2012323467', customer: 'Rahul Sharma', service: 'Domain Service', date: '01/03/2024', amount: '$1200.00', paymentStatus: 'Paid By Mastercard', status: 'Approve', icon: <Globe size={16} /> },
    { id: '2012323468', customer: 'Aditi Verma', service: 'Company Email', date: '02/03/2024', amount: '$1500.00', paymentStatus: 'Paid By Visacard', status: 'Incomplete', icon: <Mail size={16} /> },
    { id: '2012323469', customer: 'Muhammad Jaber', service: 'Cloud Hosting', date: '05/03/2024', amount: '$2200.00', paymentStatus: 'Paid By Madacard', status: 'Completed', icon: <Cloud size={16} /> },
    { id: '2012323470', customer: 'Priyanka Chopra', service: 'Digital Marketing', date: '10/03/2024', amount: '$2200.00', paymentStatus: 'Paid By Paypal', status: 'Pending', icon: <MousePointer2 size={16} /> },
    { id: '2012323471', customer: 'Amitabh Bachchan', service: 'Digital Product', date: '15/03/2024', amount: '$2200.00', paymentStatus: 'Paid By Wise', status: 'Approve', icon: <Briefcase size={16} /> },
    { id: '2012323472', customer: 'Vikram Rathore', service: 'Data Center', date: '21/03/2024', amount: '$6200.00', paymentStatus: 'Paid By Creditcard', status: 'Approve', icon: <Database size={16} /> },
    { id: '2012323473', customer: 'Suresh Raina', service: 'WordPress Hosting', date: '11/04/2024', amount: '$1200.00', paymentStatus: 'Paid By Banktransfer', status: 'Pending', icon: <Globe2 size={16} /> },
    { id: '2012323474', customer: 'Neha Kakkar', service: 'SEO Optimization', date: '12/04/2024', amount: '$1800.00', paymentStatus: 'Paid By Mastercard', status: 'Completed', icon: <Search size={16} /> },
    { id: '2012323475', customer: 'Rohit Sharma', service: 'App Development', date: '15/04/2024', amount: '$3200.00', paymentStatus: 'Paid By Paypal', status: 'Approve', icon: <Smartphone size={16} /> },
    { id: '2012323476', customer: 'Alia Bhatt', service: 'Graphic Design', date: '18/04/2024', amount: '$1400.00', paymentStatus: 'Paid By Visacard', status: 'Pending', icon: <Image size={16} /> },
    { id: '2012323477', customer: 'MS Dhoni', service: 'Video Editing', date: '20/04/2024', amount: '$2000.00', paymentStatus: 'Paid By Banktransfer', status: 'Completed', icon: <Video size={16} /> },
    { id: '2012323478', customer: 'Deepika Padukone', service: 'Social Media Marketing', date: '25/04/2024', amount: '$2500.00', paymentStatus: 'Paid By Creditcard', status: 'Approve', icon: <Users size={16} /> }
  ]);

  const getStatusStyle = (status: OrderStatus) => {
    switch (status) {
      case 'Approve': return 'bg-green-100 text-green-700';
      case 'Completed': return 'bg-blue-100 text-blue-700';
      case 'Incomplete': return 'bg-red-100 text-red-700';
      case 'Pending': return 'bg-orange-100 text-orange-700';
      default: return '';
    }
  };

  const filteredOrders = orderList.filter((order) => {
    const matchesTab = activeTab === 'All Orders' || 
                      (activeTab === 'Pending Orders' && order.status === 'Pending') ||
                      (activeTab === 'Active Orders' && order.status === 'Approve') ||
                      (activeTab === 'Cancelled Orders' && order.status === 'Incomplete');
    const matchesSearch = order.customer.toLowerCase().includes(searchQuery.toLowerCase()) || order.id.includes(searchQuery);
    return matchesTab && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="px-8 py-6 flex justify-between items-center bg-white border-b border-gray-100">
        <div className="text-sm text-gray-400">Orders / <span className="text-[#001f3f] font-bold">Manage Orders</span></div>
        <button 
          onClick={handleAddNew} 
          className="bg-[#001f3f] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#002d5c] active:scale-95 transition-all shadow-sm"
        >
          + Add New
        </button>
      </div>

      {/* Tabs & Search Row */}
      <div className="bg-white border-b border-gray-100 px-8 flex justify-between items-center h-16">
        <div className="flex gap-8 h-full items-center">
          {['All Orders', 'Active Orders', 'Pending Orders', 'Cancelled Orders'].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`text-sm font-bold h-full border-b-2 transition-all px-1 ${activeTab === tab ? 'text-[#D92323] border-[#D92323]' : 'text-gray-400 border-transparent hover:text-gray-600'}`}>{tab}</button>
          ))}
        </div>
        
        <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 w-64 transition-all focus-within:border-gray-300">
          <Search size={16} className="text-gray-400" />
          <input 
            type="text" 
            placeholder="Search..." 
            className="bg-transparent text-sm outline-none w-full ml-2 text-gray-700 placeholder-gray-400" 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="p-8">
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-xs text-gray-400 font-bold uppercase">
              <tr>
                <th className="px-6 py-4 w-10 text-center"><input type="checkbox" className="rounded border-gray-300 text-[#001f3f]" /></th>
                <th className="px-6 py-4">Order ID</th>
                <th className="px-6 py-4">Customer</th>
                <th className="px-6 py-4">Service</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Payment</th>
                <th className="px-6 py-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => (
                <tr key={order.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4 text-center"><input type="checkbox" className="rounded border-gray-300 text-[#001f3f] cursor-pointer" /></td>
                  <td className="px-6 py-4 !text-[#001f3f] font-bold cursor-pointer hover:underline" onClick={() => setSelectedOrder(order)}>{order.id}</td>
                  <td className="px-6 py-4 !text-[#001f3f] font-semibold">{order.customer}</td>
                  <td className="px-6 py-4 !text-[#001f3f] font-medium flex items-center gap-2">
                    <span className="text-gray-400">{order.icon}</span> {order.service}
                  </td>
                  <td className="px-6 py-4 !text-[#001f3f] font-medium">{order.date}</td>
                  <td className="px-6 py-4 !text-[#001f3f] font-bold">{order.amount}</td>
                  <td className="px-6 py-4 !text-[#001f3f] font-medium">{order.paymentStatus}</td>
                  <td className="px-6 py-4 text-center">
                    <select
                      value={order.status}
                      onChange={(e) =>
                        setOrderList((prev) =>
                          prev.map((o) => o.id === order.id ? { ...o, status: e.target.value as OrderStatus } : o)
                        )
                      }
                      className={`px-3 py-1 rounded text-xs font-bold border-none outline-none cursor-pointer ${getStatusStyle(order.status)}`}
                    >
                      <option value="Approve">Approve</option>
                      <option value="Incomplete">Incomplete</option>
                      <option value="Completed">Completed</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Section */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-[#001f3f]/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-[340px] shadow-2xl border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50/50">
              <h3 className="font-bold text-[#001f3f]">Order Details</h3>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:bg-gray-100 p-1 rounded-full transition-colors"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4 text-sm text-[#001f3f]">
              <p className="flex justify-between border-b border-gray-50 pb-2"><b>Order ID:</b> <span className="font-bold">{selectedOrder.id}</span></p>
              <p className="flex justify-between border-b border-gray-50 pb-2"><b>Customer:</b> <span className="font-semibold">{selectedOrder.customer}</span></p>
              <p className="flex justify-between border-b border-gray-50 pb-2"><b>Service:</b> <span className="font-medium">{selectedOrder.service}</span></p>
              <p className="flex justify-between"><b>Amount:</b> <span className="font-black text-gray-900">{selectedOrder.amount}</span></p>
              <button className="w-full bg-[#001f3f] text-white py-2 rounded-xl mt-4 font-bold hover:bg-[#002d5c] transition-all" onClick={() => setSelectedOrder(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;